import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, RefreshCw } from "lucide-react";
import { fetchCotRead } from "@/lib/api";
import { SkeletonBar } from "@/components/cot/Skeleton";

// Quick Claude read of the CFTC crowding/extremes picture. Sits under the crowding card.
export default function CotAiRead() {
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const q = useQuery({
    queryKey: ["cot-read"],
    queryFn: () => fetchCotRead(),
    staleTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
  });
  const read = (q.data?.read || "").replace(/\*\*/g, "").replace(/\*/g, "");

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const fresh = await fetchCotRead(true);
      qc.setQueryData(["cot-read"], fresh);
    } catch {
      /* ignore */
    } finally {
      setRefreshing(false);
    }
  };

  if (!q.isLoading && !read) return null;
  const busy = q.isLoading || refreshing;

  return (
    <div className="border border-zinc-800 bg-[#09090B] px-3 py-2 flex items-start gap-2" data-testid="cot-ai-read">
      <Sparkles size={12} strokeWidth={1.5} className={`text-amber-term mt-0.5 shrink-0 ${busy ? "animate-pulse" : ""}`} />
      {q.isLoading ? (
        <SkeletonBar className="h-3 w-3/4 mt-0.5" />
      ) : (
        <p className="text-[11px] leading-relaxed text-zinc-300 flex-1">
          <span className="text-zinc-600 uppercase tracking-widest text-[11px] mr-1.5">AI read</span>
          {read}
        </p>
      )}
      <button
        type="button"
        onClick={handleRefresh}
        disabled={busy}
        title="Request a fresh live read"
        data-testid="cot-ai-read-refresh"
        className="shrink-0 text-zinc-600 hover:text-amber-term transition-colors disabled:opacity-40"
      >
        <RefreshCw size={11} strokeWidth={1.75} className={refreshing ? "animate-spin" : ""} />
      </button>
    </div>
  );
}
